/**
 * Shared types and query keys for admin hooks.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AdminUser {
  id: number
  username: string
  email: string
  role: string
  created_at: string | null
  assigned_accounts: {
    id: number
    advertiser_id: string
    advertiser_name: string
  }[]
  network_slugs?: string[]
}

export interface AdminUsersResponse {
  success: boolean
  data: AdminUser[]
}

export interface AdminAccount {
  id: number
  advertiser_id: string
  advertiser_name: string
  owner_id: number | null
  is_active: boolean
  network: string | null
  deleted_at: string | null
  created_at: string | null
}

export interface AdminAccountsResponse {
  success: boolean
  data: AdminAccount[]
}

export interface TikTokAccountRow {
  id: number
  advertiser_id: string
  advertiser_name: string
  /** TikTok advertiser status from balance refresh (e.g. STATUS_ENABLE). */
  advertiser_status?: string | null
  is_active?: boolean
  deleted_at?: string | null
  balance: number | string | null
  /** Wallet / billing currency from TikTok cache (still returned for other UIs). */
  currency: string | null
  /** Sum of `daily_metrics.cost` for this advertiser, last 7 UTC calendar days (inclusive). */
  spend_7d?: number | null
  network: string | null
  is_assigned?: boolean
  assigned_username?: string
  assigned_user_id?: number | null
}

export interface TikTokAccountsListResponse {
  success: boolean
  accounts: TikTokAccountRow[]
}

export interface UserAssignmentsResponse {
  success: boolean
  data: {
    user_id: number
    username: string
    role: string
    accounts: {
      id: number
      advertiser_id: string
      advertiser_name: string
    }[]
  }
}

export interface UserSubordinatesResponse {
  success: boolean
  data: {
    user_id: number
    username: string
    role: string
    subordinates: {
      id: number
      username: string
      email: string
      role: string
    }[]
  }
}

export interface Pixel {
  id: number
  name: string
  pixel_id: string
  user_id: number
  owner_username: string
  created_at: string | null
}

export interface PixelsResponse {
  success: boolean
  data: Pixel[]
}

export interface PixelDetailsResponse {
  success: boolean
  data: {
    id: number
    name: string
    pixel_id: string
    access_token: string
    user_id: number
  }
}

export interface PixelValidateResponse {
  success: boolean
  valid: boolean
  error?: string
  message?: string
}

export interface AdNetwork {
  id: number
  name: string
  slug: string
  api_key: string | null
  base_url: string | null
  is_enabled: boolean
  created_at: string | null
  updated_at: string | null
}

export interface AdNetworksResponse {
  success: boolean
  data: AdNetwork[]
}

export interface NetworkAccountsResponse {
  success: boolean
  accounts: {
    id: string
    advertiser_id: string
    advertiser_name: string
    selected: boolean
    current_network: string | null
  }[]
}

export interface SyncStatusResponse {
  success: boolean
  job: {
    status: string
    started_at: string | null
    result: unknown
    account_ids: string[] | null
    sync_tiktok: boolean
    sync_roots: boolean
    error: string | null
  }
}

export interface ActiveSyncJobsResponse {
  success: boolean
  active_jobs: {
    job_id: string
    log_id: number
    type: string
    status: string
    job_type: string
    started_at: string | null
    description: string
    details: string | null
  }[]
  has_active_sync: boolean
}

export interface SyncTriggerResponse {
  success: boolean
  job_id: string
  status: string
  message: string
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const ADMIN_QUERY_KEYS = {
  all: ["admin"] as const,
  users: ["admin", "users"] as const,
  accounts: ["admin", "accounts"] as const,
  tiktokAccounts: ["admin", "tiktok-accounts"] as const,
  userAssignments: (id: number) => ["admin", "user-assignments", id] as const,
  userSubordinates: (id: number) =>
    ["admin", "user-subordinates", id] as const,
  pixels: ["admin", "pixels"] as const,
  pixelDetails: (id: number) => ["admin", "pixel-details", id] as const,
  adNetworks: ["admin", "ad-networks"] as const,
  networkAccounts: (slug: string) =>
    ["admin", "network-accounts", slug] as const,
  syncActive: ["admin", "sync-active"] as const,
  syncStatus: (jobId: string) => ["admin", "sync-status", jobId] as const,
  lastSync: ["admin", "last-sync"] as const,
} as const
